/**
 * Plain constants and vocabularies with no TypeBox dependency, so the browser bundle can import them
 * (via `@checkone/contract/client`) without the schema builder. The schemas in the other modules are
 * built from these lists.
 */

// ---- common ----
/** Session cookie and CSRF conventions used by the web app. */
export const SESSION_COOKIE = "co_session";
export const CSRF_COOKIE = "co_csrf";
export const CSRF_HEADER = "x-csrf-token";
export const IDEMPOTENCY_HEADER = "idempotency-key";
export const REQUEST_ID_HEADER = "x-request-id";
/** Cloudflare Turnstile token, required by the public checker after 5 checks (PLAN.md D-24, Q19). */
export const CAPTCHA_HEADER = "x-captcha-token";
export const DISCLAIMER =
  "CheckOne reports FBR's published status and keeps a dated record. It is not tax advice. Rates shown are FBR's published rates for the section; your advisor decides application.";

// ---- domain ----
export const SECTIONS = ["153(1)(a)", "153(1)(b)", "153(1)(c)", "155", "156", "233"] as const;
export const PAYEE_TYPES = ["company", "individual", "aop", "non_resident"] as const;
export const VENDOR_PAYEE_TYPES = [...PAYEE_TYPES, "unconfirmed"] as const;
export const IDENTIFIER_TYPES = ["NTN", "CNIC", "STRN"] as const;
export const LIST_SOURCES = ["ATL_IT", "ATL_ST"] as const;
/** Both ATL files are membership lists (PLAN.md D-30): listed = active. There is no suspension status. */
export const LIST_STATUSES = ["active", "not_listed"] as const;
/** A vendor's status on one list: `not_checked` when it has no identifier that list can carry. */
export const VENDOR_LIST_STATUSES = ["active", "not_listed", "not_checked"] as const;
/**
 * `pending_first_check`: the vendor's NTN or CNIC as they are now have not been checked yet (a new vendor,
 * or one whose identifiers changed since its last check); a check is queued and the app shows "Checking…".
 */
export const NOT_CHECKED_REASONS = [
  "no_identifier",
  "strn_only",
  "no_current_file",
  "pending_first_check",
] as const;
export const CHANGE_TYPES = ["dropped_off", "added"] as const;
export const RATE_UNAVAILABLE_REASONS = [
  "atl_unknown",
  "payee_unconfirmed",
  "section_out_of_scope",
  "not_in_table",
  "no_approved_table",
] as const;
export const ROLES = ["owner", "member", "viewer"] as const;
export const TENANT_KINDS = ["company", "practitioner"] as const;
export const TENANT_STATUSES = ["trial", "active", "past_due", "paused", "cancelled"] as const;
export const FLAG_KEYS = [
  "global_pause",
  "monday_send",
  "public_checker",
  "billing",
  "whatsapp",
  "whatsapp_attention",
] as const;
export const CHANNELS = ["whatsapp", "email"] as const;

// ---- areas/admin ----
export const SOURCE_FILE_STATUSES = ["pending", "ok", "quarantined", "released"] as const;
export const RATE_TABLE_STATUSES = ["proposed", "approved", "superseded", "rejected"] as const;

// ---- areas/billing ----
export const PAYMENT_METHODS = ["card", "jazzcash", "easypaisa", "bank_transfer"] as const;
export const INVOICE_STATUSES = ["issued", "paid", "void", "refunded", "credited"] as const;
/** Where an invoice came from; null for invoices issued before this was recorded. */
export const INVOICE_ORIGINS = ["checkout", "bank_transfer", "renewal", "proration"] as const;
export const MESSAGE_STATUSES = ["queued", "sent", "delivered", "read", "failed", "fell_back"] as const;

// ---- areas/uploads ----
export const MAX_UPLOAD_BYTES = 10 * 1024 * 1024;
export const UPLOAD_EXTENSIONS = [".xlsx", ".xls", ".csv"] as const;
export const PREVIEW_SAMPLE_SIZE = 50;
/**
 * Columns a vendor list can map. Name plus at least one identifier is required. `annual_spend` is optional:
 * the customer's own yearly spend with the vendor, used only for the share of spend held by vendors that
 * cannot be checked (never to compute tax).
 */
export const MAPPING_FIELDS = ["name", "ntn", "cnic", "strn", "category", "section", "annual_spend"] as const;
export type MappingField = (typeof MAPPING_FIELDS)[number];
export const UPLOAD_STATUSES = ["uploaded", "processing", "ready", "committed", "failed", "expired"] as const;
export const INVALID_REASONS = [
  "blank",
  "sci_notation_lossy",
  "ntn8_undashed",
  "malformed",
  "duplicate_in_file",
  "duplicate_existing",
  "ntn_cnic_disagree",
] as const;

// ---- areas/practice ----
/** Most clients one Practice Watch request may add (core PRACTICE_WATCH_MAX_BATCH); the plan limit still applies. */
export const PRACTICE_WATCH_MAX_BATCH = 250;

// ---- areas/runs ----
export const MESSAGE_TEMPLATES = [
  "first_check",
  "monday_changes",
  "monday_quiet",
  "monday_stale",
  "reset_alert",
  "correction",
  "trial_ending",
  "payment_failed",
  "practitioner_summary",
  "monday_attention",
  "practice_watch",
] as const;
/**
 * Why a status change was marked actioned (the "actioned" trail). The customer's own record of what they
 * did; `switched_rate` reads "switched to the non-ATL rate" for a vendor that dropped off the income-tax
 * ATL and "switched back to the ATL rate" for one that re-entered. `other` needs a note.
 */
export const ACK_REASONS = [
  "switched_rate",
  "payment_held",
  "vendor_contacted",
  "no_payment_due",
  "other",
] as const;
export type AckReason = (typeof ACK_REASONS)[number];
export const ACK_NOTE_MAX = 500;
/**
 * How far back "not marked actioned" looks, in the Monday message and the app: changes reported in the last
 * five weeks before the current check. Older ones stay in the evidence log and on each vendor's page.
 */
export const UNACTIONED_LOOKBACK_DAYS = 35;
/**
 * `correction`: supersedes a run whose message was wrong; the original run is never edited.
 * `vendor_check`: a small run over the vendors whose NTN or CNIC was added or changed (vendor add or edit,
 * an accepted name suggestion), against the current FBR files; it sends no message.
 */
export const RUN_KINDS = ["weekly", "first_check", "correction", "vendor_check"] as const;
/** `stale`: FBR published no new list since the previous run; last known status shown (monday_stale). */
export const RUN_STATUSES = ["completed", "stale", "failed"] as const;
export const EXPORT_KINDS = [
  "vendor_history",
  "period_statement",
  "file_certificate",
  "s161_pack",
  "workspace_data",
  "vendor_list",
  "payment_check",
] as const;
export const EXPORT_FORMATS = ["pdf", "xlsx", "zip", "csv"] as const;
export const EXPORT_STATUSES = ["queued", "running", "ready", "failed"] as const;

// ---- areas/vendors ----
export const VENDOR_FILTERS = [
  "all",
  "changed",
  "not_on_atl",
  "not_on_st",
  "needs_identifier",
  "payee_unconfirmed",
  "inactive",
] as const;
export const IDENTIFIER_ISSUES = ["ntn_cnic_disagree", "malformed", "duplicate"] as const;
/**
 * Status on a payment date (same rule as the section 161 pack: the FBR file in force on the date).
 * - `recorded`: on or after CheckOne's first check of this vendor
 * - `before_first_check`: CheckOne had not checked this vendor yet; the status is read from CheckOne's
 *   record of the FBR file in force, as the pack does
 * - `before_capture`: no FBR file of either list was captured on or before the date: no status recorded
 * - `no_identifier`: the vendor has no NTN or CNIC, so FBR's lists cannot be matched
 */
export const STATUS_ON_OUTCOMES = [
  "recorded",
  "before_first_check",
  "before_capture",
  "no_identifier",
] as const;
/** Why one list has no status on the date. */
export const STATUS_ON_REASONS = ["no_identifier", "strn_only", "before_capture"] as const;

// ---- areas/payments ----
/** At most this many rows in one pre-payment-run check. */
export const PAYMENT_CHECK_MAX_ROWS = 1000;
/** A payment date may be up to this many days after today (PKT): the files in force today are used. */
export const PAYMENT_DATE_MAX_AHEAD_DAYS = 31;
/** Where the rows came from: pasted text, an uploaded file, or vendors picked from the list. */
export const PAYMENT_INPUT_KINDS = ["paste", "file", "list"] as const;
/**
 * Flags on one row of a payment run check:
 * - `dropped_off_since_last_payment`: on a list at this payee's previous recorded payment check (or, before
 *   the first one, its latest weekly check) and not on it now
 * - `no_identifier`: no NTN or CNIC to match against FBR's lists (blank, or an STRN only)
 * - `identifier_unreadable`: the payee identifier as typed is not a valid NTN, CNIC or STRN
 * - `not_in_vendor_list`: no vendor on the workspace's list has this identifier
 * - `payment_date_ahead`: the payment date is after today; the status is from the files in force today
 */
export const PAYMENT_CHECK_FLAGS = [
  "dropped_off_since_last_payment",
  "no_identifier",
  "identifier_unreadable",
  "not_in_vendor_list",
  "payment_date_ahead",
] as const;
/** Where a row's section came from: the row itself, the vendor's default, or the workspace default. */
export const PAYMENT_SECTION_FROM = ["row", "vendor", "workspace"] as const;
/** Why a payment run check cannot be run now (the list's `entitlement.reason`). */
export const PAYMENT_CHECK_UNAVAILABLE = ["plan", "inactive"] as const;
